import { PageLayout } from "@transfr-inc/dashboard-components/layouts";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  MenuOptionSpec,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Role as ClientRole } from "@transfr-inc/dashboard-components/utils";

import {
  DeleteUserModal,
  EditOrganizationModal,
  EditUserModal,
  ResendInviteModal,
} from "@components/modals";
import {
  MULTI_ORG_CHILD,
  MULTI_ORG_PARENT,
} from "@constants/organization-types";
import { useNotification } from "@hooks/use-notification";
import { services } from "@src/dependencies";
import { catchEditUserError } from "@src/services";
import { useApiRequest } from "@utils/http-client";
import { getSearchParamValue } from "@utils/navigation";

import { OrgDetailPageHeader } from "./components/page.header";
import { PageTabs } from "./components/page.tabs";
import { getRoleMap } from "./utils/user-roles.utils";

import "./index.scss";

export const OrganizationDetail = ({ responsiveStyles }) => {
  const { addNotification } = useNotification();
  const { code } = useParams();
  const location = useLocation();
  const breadcrumbLabel = getSearchParamValue(
    location,
    "from",
    "All Organizations",
  );

  const {
    classroomService,
    organizationService,
    userService,
    authenticationService,
  } = services;

  // For edit organization modal
  const [orgSelected, setOrgSelected] = useState({});
  const [openEditOrg, setOpenEditOrg] = useState(false);

  // For delete user
  const [deletedUser, setDeletedUser] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState();

  // For edit user modal
  const [selectedUser, setSelectedUser] = useState();
  const [openEditUserModal, setOpenEditUserModal] = useState();

  // For resend invite
  const [openInviteModal, setOpenInviteModal] = useState();

  const {
    loading: loadingOrg,
    response: organization,
    sendRequest: getOrganizationDetails,
    resetResponse,
  } = useApiRequest(() => {
    resetResponse();
    return organizationService.getOrganizationDetails(code);
  });

  const {
    response: parentOrg,
    sendRequest: getParentOrg,
    resetResponse: resetParentOrg,
  } = useApiRequest((code) => {
    resetParentOrg();
    return organizationService
      .getParentOrgs([code])
      .then((res) => (res?.length > 0 ? res[0] : undefined));
  }, false);

  const {
    response: classrooms,
    sendRequest: getClassrooms,
    resetResponse: resetClassrooms,
  } = useApiRequest(() => classroomService.getClassrooms(code, { includeChildOrgClassrooms: true }), false);

  const {
    loading: loadingUsers,
    response: users,
    sendRequest: getClientUsers,
    resetResponse: resetUsers,
  } = useApiRequest(({ code, typeId }) => {
    resetUsers();
    return userService.getOrganizationUsers(code, typeId).then(getRoleMap);
  }, false);

  const {
    response: childOrgs,
    loading: loadingChildOrgs,
    sendRequest: getChildOrgs,
    resetResponse: resetChildOrgs,
  } = useApiRequest(() => organizationService.getChildOrgs(code), false);

  const fetchOrganizationDetails = async () => {
    resetParentOrg();
    resetUsers();
    resetChildOrgs();

    const org = await getOrganizationDetails();
    if (org.typeId === MULTI_ORG_PARENT.id) {
      await getChildOrgs();
    } else if (org.typeId === MULTI_ORG_CHILD.id) {
      await getParentOrg(org.code);
    }

    getClientUsers(org);
  };

  useEffect(() => {
    if (code) {
      resetClassrooms();
      fetchOrganizationDetails();
      getClassrooms();
    }
  }, [code]);

  async function launchImpersonation(
    rowData,
    destination = process.env.DASHBOARD_URL,
  ) {
    try {
      await authenticationService.impersonate(rowData[0], destination);
    } catch {
      showNotification({
        type: NotificationType.error,
        message: <span>Something went wrong.</span>,
      });
    }
  }

  const onUserCreated = (message) => {
    showNotification({ type: NotificationType.success, message });
    getClientUsers(organization);
  };

  const showNotification = ({ type, message }) => {
    addNotification({ type, message, ttl: 5000 });
  };

  // Edit Child Organization
  const onOpenEditOrg = async (code, data) => {
    const orgDetails = await organizationService.getOrganizationDetails(code);
    setOrgSelected({
      ...orgDetails,
      products: data.products,
      parent: organization,
    });
    setOpenEditOrg(true);
  };

  const onCloseEditOrg = () => {
    setOpenEditOrg(false);
  };

  const onUpdateOrganization = async (data) => {
    fetchOrganizationDetails();
    setOpenEditOrg();
    showNotification({
      type: NotificationType.success,
      message: (
        <span>
          The details for <b>{data.name}</b> have been updated.
        </span>
      ),
    });
  };

  // Delete User
  const onDeleteUser = async (_, value) => {
    setDeletedUser([value]);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteUsers = () => {
    setOpenDeleteModal();
  };

  const onConfirmDeleteUsers = async (users) => {
    try {
      const userIds = users.map((x) => x["userId"]);
      await userService.deleteUsers({
        userIds: userIds,
        roleId: users[0]["roleId"],
      });
      onCloseDeleteUsers();
      getClientUsers(organization);
      showNotification({
        type: NotificationType.success,
        message: <div>User has been deleted</div>,
      });
    } catch {
      showNotification({
        type: NotificationType.error,
        message: "Something went wrong",
      });
    }
  };

  //Edit User
  const onOpenEditUser = async (data) => {
    const user = await userService.getUserDetails(data[0]);
    setSelectedUser(user);
    setOpenEditUserModal(true);
  };
  const onCloseEditDetails = () => {
    setOpenEditUserModal();
    setSelectedUser();
  };

  const onUpdateUser = async (user) => {
    try {
      const updatedUser = await userService.updateUserMetadata(user);
      showNotification({
        type: NotificationType.success,
        message: `User ${updatedUser.firstName} ${updatedUser.lastName} has been updated successfully.`,
        icon: ["fa-solid", "circle-check"],
      });

      onCloseEditDetails();
      getClientUsers(organization);
      return updatedUser;
    } catch (error) {
      console.error(error);
      user.error = catchEditUserError(
        error,
        user.roleId == ClientRole.STUDENT.id,
      );
      return user;
    }
  };

  // Resend Invite
  const onOpenInviteUser = async (_, value) => {
    setSelectedUser(value);
    setOpenInviteModal(true);
  };
  const onInvitationSentHandler = () => {
    setOpenInviteModal();
    showNotification({
      type: NotificationType.success,
      message: (
        <div>
          An account creation email has been sent to
          <b>
            {" "}
            {selectedUser.firstName} {selectedUser.lastName}
          </b>
        </div>
      ),
      icon: ["fa-solid", "circle-check"],
    });
    setSelectedUser();
  };
  const onCloseResendInvite = () => {
    setOpenInviteModal();
    setSelectedUser();
  };

  const actionMenuOptions = {
    delete: new MenuOptionSpec(
      "Delete",
      ["fa-regular", "fa-trash-can"],
      onDeleteUser,
    ),
    impersonate: new MenuOptionSpec(
      "Impersonate",
      ["fa-regular", "fa-mask"],
      (rowData) => launchImpersonation(rowData),
    ),
    impersonateTrek: new MenuOptionSpec(
      "Impersonate (Trek)",
      ["fa-regular", "fa-mask"],
      (rowData) => launchImpersonation(rowData, process.env.CESE_URL),
      "impersonate-trek",
    ),
    edit: new MenuOptionSpec(
      "Edit Details",
      ["fa-regular", "fa-edit"],
      onOpenEditOrg,
    ),
    editUser: new MenuOptionSpec(
      "Edit Details",
      ["fa-regular", "fa-edit"],
      onOpenEditUser,
    ),
    sendInvite: new MenuOptionSpec(
      "Send Invitation",
      ["fa-regular", "fa-paper-plane"],
      onOpenInviteUser,
    ),
  };

  return (
    <PageLayout
      className="organization-detail-page"
      drawerColorEnabled
      header={
        <OrgDetailPageHeader
          organization={organization}
          breadcrumb={breadcrumbLabel}
          onUserCreated={onUserCreated}
          onOrgUpdated={onUpdateOrganization}
          parentOrg={parentOrg}
        />
      }
    >
      <PageTabs
        orgType={organization?.typeId}
        organization={organization}
        parentOrg={parentOrg}
        classrooms={classrooms}
        users={users}
        childOrgs={childOrgs}
        responsiveStyles={responsiveStyles}
        isLoading={loadingOrg || loadingChildOrgs || loadingUsers}
        actionMenuOptions={actionMenuOptions}
      />
      <EditOrganizationModal
        open={openEditOrg}
        onClose={onCloseEditOrg}
        organization={orgSelected}
        onUpdateOrg={onUpdateOrganization}
      ></EditOrganizationModal>
      <DeleteUserModal
        open={openDeleteModal}
        users={deletedUser}
        onCancel={onCloseDeleteUsers}
        onConfirmDelete={onConfirmDeleteUsers}
        clientUser
        async
      ></DeleteUserModal>
      <EditUserModal
        open={openEditUserModal}
        onClose={onCloseEditDetails}
        user={selectedUser}
        onUpdateUser={onUpdateUser}
        clientUser
      ></EditUserModal>
      <ResendInviteModal
        open={openInviteModal}
        userId={selectedUser?.userId}
        onClose={onCloseResendInvite}
        onInvitationSent={onInvitationSentHandler}
      ></ResendInviteModal>
    </PageLayout>
  );
};
